import React, { useEffect, useId, useRef, useState } from "react";

/**
 * Custom compact select — avoids Android WebView native picker (huge fonts/rows).
 * options: [{ value: string|number, label: string }]
 */
export default function CompactSelect({
    value,
    onChange,
    options = [],
    placeholder = "Оберіть…",
    className = "",
    ariaLabel,
    disabled = false,
}) {
    const [open, setOpen] = useState(false);
    const rootRef = useRef(null);
    const listId = useId();
    const normalizedValue = value === null || value === undefined ? "" : String(value);
    const selected = options.find((item) => String(item.value) === normalizedValue);
    const label = selected?.label || placeholder;

    useEffect(() => {
        if (!open) return undefined;
        const onPointerDown = (event) => {
            if (!rootRef.current?.contains(event.target)) {
                setOpen(false);
            }
        };
        const onKeyDown = (event) => {
            if (event.key === "Escape") {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", onPointerDown);
        document.addEventListener("touchstart", onPointerDown);
        document.addEventListener("keydown", onKeyDown);
        return () => {
            document.removeEventListener("mousedown", onPointerDown);
            document.removeEventListener("touchstart", onPointerDown);
            document.removeEventListener("keydown", onKeyDown);
        };
    }, [open]);

    return (
        <div
            ref={rootRef}
            className={`compact-select ${open ? "is-open" : ""} ${className}`.trim()}
        >
            <button
                type="button"
                className="compact-select__trigger"
                aria-haspopup="listbox"
                aria-expanded={open}
                aria-controls={listId}
                aria-label={ariaLabel || placeholder}
                disabled={disabled}
                onClick={() => {
                    if (!disabled) setOpen((prev) => !prev);
                }}
            >
                <span className={`compact-select__label ${selected ? "" : "is-placeholder"}`}>
                    {label}
                </span>
                <span className="compact-select__chevron" aria-hidden="true" />
            </button>
            {open ? (
                <ul
                    id={listId}
                    className="compact-select__menu"
                    role="listbox"
                    aria-label={ariaLabel || placeholder}
                >
                    {options.map((item) => {
                        const itemValue = String(item.value);
                        const isActive = itemValue === normalizedValue;
                        return (
                            <li key={`${itemValue}-${item.label}`}>
                                <button
                                    type="button"
                                    role="option"
                                    aria-selected={isActive}
                                    className={`compact-select__option ${isActive ? "is-active" : ""}`}
                                    onClick={() => {
                                        onChange(item.value);
                                        setOpen(false);
                                    }}
                                >
                                    <span>{item.label}</span>
                                    {isActive ? <span className="compact-select__check" aria-hidden="true">✓</span> : null}
                                </button>
                            </li>
                        );
                    })}
                </ul>
            ) : null}
        </div>
    );
}
